import React, { useState, useMemo } from 'react';
import {
  Send, MessageSquare, Phone, Users, Search, CheckCircle2,
  Clock, AlertCircle, X, ChevronDown, RefreshCw, Plus,
  Smartphone, Mail, Filter, Calendar, TrendingUp, Eye,
  Package, Truck, Bell, FileText
} from 'lucide-react';

// Mock tenants data
const mockTenants = [
  { id: 'T001', name: 'FastShip Jamaica', phone: '+1 876 555-0101', email: 'ops@fastship.jm', status: 'active', packages: 127 },
  { id: 'T002', name: 'SpeedyCourier', phone: '+1 876 555-0102', email: 'admin@speedycourier.jm', status: 'active', packages: 89 },
  { id: 'T003', name: 'Island Express', phone: '+1 876 555-0103', email: 'contact@islandexp.jm', status: 'active', packages: 156 },
  { id: 'T004', name: 'Caribbean Cargo', phone: '+1 876 555-0104', email: 'ops@caribcargo.jm', status: 'active', packages: 203 },
  { id: 'T005', name: 'JA Quick Delivery', phone: '+1 876 555-0105', email: 'info@jaquick.jm', status: 'active', packages: 45 },
  { id: 'T006', name: 'Kingston Logistics', phone: '+1 876 555-0106', email: 'support@kingstonlog.jm', status: 'inactive', packages: 0 },
  { id: 'T007', name: 'MoBay Movers', phone: '+1 876 555-0107', email: 'hello@mobaymovers.jm', status: 'active', packages: 78 },
  { id: 'T008', name: 'Portmore Parcels', phone: '+1 876 555-0108', email: 'team@portparcels.jm', status: 'active', packages: 112 },
];

// Notification templates
const templates = [
  { 
    id: 'shipment_cleared', 
    name: 'Shipment Cleared', 
    icon: Package,
    category: 'operational',
    message: 'Your shipment has cleared customs and is now at the KP warehouse. Ready for collection or delivery scheduling.'
  },
  { 
    id: 'ready_pickup', 
    name: 'Ready for Pickup', 
    icon: Truck,
    category: 'operational',
    message: 'Packages are ready for pickup at the KP warehouse. Please collect within 48 hours to avoid storage fees.'
  },
  { 
    id: 'manifest_available', 
    name: 'Manifest Available', 
    icon: FileText,
    category: 'operational',
    message: 'New manifest has been processed and is now available in your dashboard. Please review and confirm package details.'
  },
  { 
    id: 'invoice_generated', 
    name: 'Invoice Generated', 
    icon: Bell,
    category: 'billing',
    message: 'Your weekly invoice has been generated. Please review charges and make payment by the due date.'
  },
  { 
    id: 'payment_reminder', 
    name: 'Payment Reminder', 
    icon: AlertCircle,
    category: 'billing',
    message: 'Friendly reminder: You have an outstanding balance. Please make payment to avoid service interruption.'
  },
  { 
    id: 'general', 
    name: 'General Announcement', 
    icon: MessageSquare,
    category: 'general',
    message: ''
  },
];

// Mock broadcast history
const mockHistory = [
  { id: 'B001', template: 'Shipment Cleared', recipients: 8, channels: ['sms', 'whatsapp'], sentAt: '2025-03-05 14:30', status: 'delivered' },
  { id: 'B002', template: 'Ready for Pickup', recipients: 5, channels: ['whatsapp'], sentAt: '2025-03-05 10:15', status: 'delivered' },
  { id: 'B003', template: 'Invoice Generated', recipients: 8, channels: ['sms', 'whatsapp', 'email'], sentAt: '2025-03-04 09:00', status: 'delivered' },
  { id: 'B004', template: 'General Announcement', recipients: 8, channels: ['whatsapp'], sentAt: '2025-03-03 16:45', status: 'partial' },
  { id: 'B005', template: 'Payment Reminder', recipients: 3, channels: ['sms'], sentAt: '2025-03-02 11:30', status: 'delivered' },
];

// Stats component
const StatCard = ({ title, value, subtitle, icon: Icon, colorClass }) => (
  <div className={`broadcast-stat-card ${colorClass || ''}`} data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="stat-icon-wrapper">
      <Icon size={20} />
    </div>
    <div className="stat-content">
      <span className="stat-value">{value}</span>
      <span className="stat-title">{title}</span>
      {subtitle && <span className="stat-subtitle">{subtitle}</span>}
    </div>
  </div>
);

// Tenant Selection Item
const TenantItem = ({ tenant, selected, onToggle }) => (
  <div 
    className={`tenant-select-item ${selected ? 'selected' : ''}`}
    onClick={() => onToggle(tenant.id)}
  >
    <div className="tenant-checkbox">
      {selected && <CheckCircle2 size={16} />}
    </div>
    <div className="tenant-info">
      <span className="tenant-name">{tenant.name}</span>
      <span className="tenant-contact">{tenant.phone}</span>
    </div>
    <div className="tenant-packages">
      <Package size={14} />
      <span>{tenant.packages}</span>
    </div>
    <span className={`tenant-status ${tenant.status}`}>{tenant.status}</span>
  </div>
);

// History Row
const HistoryRow = ({ broadcast }) => (
  <div className="history-row">
    <div className="history-template">{broadcast.template}</div>
    <div className="history-recipients">{broadcast.recipients} tenants</div>
    <div className="history-channels">
      {broadcast.channels.map(ch => (
        <span key={ch} className={`channel-badge ${ch}`}>
          {ch === 'sms' && <Phone size={12} />}
          {ch === 'whatsapp' && <MessageSquare size={12} />}
          {ch === 'email' && <Mail size={12} />}
          {ch}
        </span>
      ))}
    </div>
    <div className="history-time">{broadcast.sentAt}</div>
    <div className={`history-status ${broadcast.status}`}>
      {broadcast.status === 'delivered' && <CheckCircle2 size={14} />}
      {broadcast.status === 'partial' && <AlertCircle size={14} />}
      {broadcast.status}
    </div>
  </div>
);

export default function Broadcast() {
  const [activeTab, setActiveTab] = useState('compose');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [channels, setChannels] = useState({ sms: true, whatsapp: true, email: false });
  const [customMessage, setCustomMessage] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  // Filter tenants
  const filteredTenants = useMemo(() => {
    return mockTenants.filter(tenant => {
      const matchesSearch = !searchQuery ||
        tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tenant.phone.includes(searchQuery);
      const matchesStatus = filterStatus === 'all' || tenant.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, filterStatus]);

  // Stats
  const stats = useMemo(() => ({
    totalTenants: mockTenants.length,
    activeTenants: mockTenants.filter(t => t.status === 'active').length,
    sentToday: mockHistory.filter(h => h.sentAt.startsWith('2025-03-05')).length,
    totalSent: mockHistory.length
  }), []);

  // Toggle tenant selection
  const toggleTenant = (tenantId) => {
    setSelectedTenants(prev => 
      prev.includes(tenantId) 
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  // Select all tenants
  const selectAllTenants = () => {
    const activeTenantIds = filteredTenants.filter(t => t.status === 'active').map(t => t.id);
    setSelectedTenants(activeTenantIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedTenants([]);
  };

  // Toggle channel
  const toggleChannel = (channel) => {
    setChannels(prev => ({ ...prev, [channel]: !prev[channel] }));
  };

  // Get message content
  const getMessage = () => {
    if (selectedTemplate?.id === 'general') {
      return customMessage;
    }
    return selectedTemplate?.message || '';
  };

  // Send broadcast
  const handleSend = () => {
    setSending(true);
    // Simulate sending
    setTimeout(() => {
      setSending(false);
      setShowConfirmDialog(false);
      alert(`Broadcast sent successfully to ${selectedTenants.length} tenants!`);
      // Reset form
      setSelectedTemplate(null);
      setSelectedTenants([]);
      setCustomMessage('');
    }, 2000);
  };

  const canSend = selectedTemplate && selectedTenants.length > 0 && 
    (channels.sms || channels.whatsapp || channels.email) &&
    (selectedTemplate.id !== 'general' || customMessage.trim());

  return (
    <div className="broadcast-page" data-testid="broadcast-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="header-icon">
            <Send size={28} />
          </div>
          <div className="header-text">
            <h1>Broadcast Communications</h1>
            <p>Send batch notifications to tenants via SMS & WhatsApp</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-secondary">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="broadcast-stats-grid">
        <StatCard title="Total Tenants" value={stats.totalTenants} icon={Users} />
        <StatCard title="Active Tenants" value={stats.activeTenants} icon={CheckCircle2} colorClass="stat-green" />
        <StatCard title="Sent Today" value={stats.sentToday} icon={Send} colorClass="stat-blue" />
        <StatCard title="Total Broadcasts" value={stats.totalSent} icon={TrendingUp} colorClass="stat-purple" />
      </div>

      {/* Tabs */}
      <div className="broadcast-content">
        <div className="broadcast-tabs">
          <button 
            className={`tab-btn ${activeTab === 'compose' ? 'active' : ''}`}
            onClick={() => setActiveTab('compose')}
          >
            <Plus size={16} />
            Compose
          </button>
          <button 
            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <Clock size={16} />
            History
          </button>
        </div>

        {/* Compose Tab */}
        {activeTab === 'compose' && (
          <div className="compose-section">
            <div className="compose-grid">
              {/* Left: Template Selection */}
              <div className="compose-panel">
                <h3>1. Select Notification Type</h3>
                <div className="templates-grid">
                  {templates.map(template => (
                    <div 
                      key={template.id}
                      className={`template-card ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <div className={`template-icon ${template.category}`}>
                        <template.icon size={20} />
                      </div>
                      <span className="template-name">{template.name}</span>
                      <span className={`template-category ${template.category}`}>{template.category}</span>
                    </div>
                  ))}
                </div>

                {/* Message Preview/Edit */}
                {selectedTemplate && (
                  <div className="message-section">
                    <h4>Message {selectedTemplate.id === 'general' ? '(Custom)' : 'Preview'}</h4>
                    {selectedTemplate.id === 'general' ? (
                      <textarea
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        placeholder="Type your custom message here..."
                        className="message-textarea"
                        rows={4}
                      />
                    ) : (
                      <div className="message-preview">
                        {selectedTemplate.message}
                      </div>
                    )}
                  </div>
                )}

                {/* Channel Selection */}
                <div className="channels-section">
                  <h4>2. Select Channels</h4>
                  <div className="channels-grid">
                    <button 
                      className={`channel-btn ${channels.sms ? 'active' : ''}`}
                      onClick={() => toggleChannel('sms')}
                    >
                      <Phone size={18} />
                      <span>SMS</span>
                      {channels.sms && <CheckCircle2 size={14} className="check-icon" />}
                    </button>
                    <button 
                      className={`channel-btn ${channels.whatsapp ? 'active' : ''}`}
                      onClick={() => toggleChannel('whatsapp')}
                    >
                      <MessageSquare size={18} />
                      <span>WhatsApp</span>
                      {channels.whatsapp && <CheckCircle2 size={14} className="check-icon" />}
                    </button>
                    <button 
                      className={`channel-btn ${channels.email ? 'active' : ''}`}
                      onClick={() => toggleChannel('email')}
                    >
                      <Mail size={18} />
                      <span>Email</span>
                      {channels.email && <CheckCircle2 size={14} className="check-icon" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Right: Tenant Selection */}
              <div className="compose-panel">
                <div className="panel-header">
                  <h3>3. Select Recipients</h3>
                  <div className="selection-actions">
                    <button className="btn-text" onClick={selectAllTenants}>Select All Active</button>
                    <button className="btn-text" onClick={clearSelection}>Clear</button>
                  </div>
                </div>

                {/* Search & Filter */}
                <div className="tenant-filters">
                  <div className="search-input">
                    <Search size={16} />
                    <input
                      type="text"
                      placeholder="Search tenants..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <select 
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="status-filter"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {/* Selected Count */}
                <div className="selection-info">
                  <span className="selected-count">
                    <CheckCircle2 size={16} />
                    {selectedTenants.length} selected
                  </span>
                </div>

                {/* Tenant List */}
                <div className="tenant-list">
                  {filteredTenants.map(tenant => (
                    <TenantItem
                      key={tenant.id}
                      tenant={tenant}
                      selected={selectedTenants.includes(tenant.id)}
                      onToggle={toggleTenant}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Send Button */}
            <div className="compose-footer">
              <div className="send-summary">
                {canSend && (
                  <span>
                    Sending <strong>{selectedTemplate?.name}</strong> to <strong>{selectedTenants.length} tenants</strong> via{' '}
                    {[channels.sms && 'SMS', channels.whatsapp && 'WhatsApp', channels.email && 'Email'].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
              <button 
                className="btn-primary btn-send"
                disabled={!canSend}
                onClick={() => setShowConfirmDialog(true)}
              >
                <Send size={18} />
                Send Broadcast
              </button>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="history-section">
            <div className="history-header">
              <div className="col">Template</div>
              <div className="col">Recipients</div>
              <div className="col">Channels</div>
              <div className="col">Sent At</div>
              <div className="col">Status</div>
            </div>
            <div className="history-list">
              {mockHistory.map(broadcast => (
                <HistoryRow key={broadcast.id} broadcast={broadcast} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="dialog-overlay" onClick={() => setShowConfirmDialog(false)}>
          <div className="dialog-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>Confirm Broadcast</h2>
              <button className="close-btn" onClick={() => setShowConfirmDialog(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="dialog-body">
              <div className="confirm-summary">
                <div className="confirm-item">
                  <span className="label">Notification Type</span>
                  <span className="value">{selectedTemplate?.name}</span>
                </div>
                <div className="confirm-item">
                  <span className="label">Recipients</span>
                  <span className="value">{selectedTenants.length} tenants</span>
                </div>
                <div className="confirm-item">
                  <span className="label">Channels</span>
                  <span className="value">
                    {[channels.sms && 'SMS', channels.whatsapp && 'WhatsApp', channels.email && 'Email'].filter(Boolean).join(', ')}
                  </span>
                </div>
              </div>
              <div className="message-preview-box">
                <h4>Message</h4>
                <p>{getMessage()}</p>
              </div>
              <div className="confirm-warning">
                <AlertCircle size={16} />
                <span>This action will send notifications to all selected tenants. This cannot be undone.</span>
              </div>
            </div>
            <div className="dialog-footer">
              <button className="btn-secondary" onClick={() => setShowConfirmDialog(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleSend}
                disabled={sending}
              >
                {sending ? (
                  <>
                    <RefreshCw size={16} className="spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Confirm & Send
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
